import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AuthUserContext,
  withAuthorization,
  withEmailVerification,
} from '../Session';
import { withFirebase } from '../Firebase';
import { PasswordForgetForm } from '../PasswordForget';
import PasswordChangeForm from '../PasswordChange';
import CreateOrgModal from './CreateOrgModal';
import './Account.css';

const SIGN_IN_METHODS = [
  {
    id: 'password',
    provider: null,
  },
  {
    id: 'google.com',
    provider: 'googleProvider',
  },
  {
    id: 'microsoft.com',
    provider: 'microsoftProvider',
  },
];

const TABS = [
  { id: 'profile', labelKey: 'account.tab_profile' },
  { id: 'organizations', labelKey: 'account.tab_organizations' },
  { id: 'security', labelKey: 'account.tab_security' },
  { id: 'connections', labelKey: 'account.tab_connections' },
];

// Helper to get avatar info - same approach as Navigation
const getAvatarInfo = (authUser) => {
  const providerData = authUser?.providerData?.[0];
  const hasPhoto = Boolean(providerData?.photoURL);
  const photoURL = providerData?.photoURL || authUser?.photoURL;
  const displayName = providerData?.displayName || authUser?.username || '';
  const email = providerData?.email || authUser?.email || '';

  const initials = displayName
    ? displayName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    : email ? email[0].toUpperCase() : '?';

  return { hasPhoto, photoURL, displayName, email, initials };
};

const getMemberInitials = (member) => {
  const name = member.username || member.email || '';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

// ── Profile Tab ──────────────────────────────────────────────────────────────
// Personal info only: display name edit, read-only email + UID
const ProfileTab = withFirebase(({ authUser, firebase, t }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [username, setUsername] = useState(authUser.username || '');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setUsername(authUser.username || '');
  }, [authUser.username]);

  const handleSave = async () => {
    if (!username.trim()) {
      setMessage({ type: 'error', text: t('account.username_required') });
      return;
    }

    setLoading(true);
    try {
      await firebase.user(authUser.uid).update({ username: username.trim() });
      setMessage({ type: 'success', text: t('account.profile_updated') });
      setIsEditing(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setUsername(authUser.username || '');
    setIsEditing(false);
    setMessage({ type: '', text: '' });
  };

  const avatarInfo = getAvatarInfo(authUser);

  return (
    <div className="profile-tab-layout">
      <div className="profile-tab-main">
        <div className="account-card accent-primary">
          <div className="card-header-row">
            <h5 className="section-card-title">{t('account.profile_information')}</h5>
            {!isEditing && (
              <button
                className="btn btn-primary"
                onClick={() => setIsEditing(true)}
              >
                {t('account.edit_profile')}
              </button>
            )}
          </div>

          {message.text && (
            <div className={`message ${message.type}`}>
              {message.type === 'success' ? '✓' : '⚠'} {message.text}
            </div>
          )}

          <div className="profile-form">
            <div className="form-group">
              <label>{t('account.display_name')}</label>
              {isEditing ? (
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('account.enter_name')}
                  autoFocus
                />
              ) : (
                <input
                  type="text"
                  value={avatarInfo.displayName || authUser.username || t('account.not_set')}
                  disabled
                />
              )}
            </div>

            <div className="form-group">
              <label>{t('email')}</label>
              <input
                type="email"
                value={authUser.email}
                disabled
              />
            </div>

            <p className="uid-line">
              {t('account.user_id')}: <code>{authUser.uid}</code>
            </p>

            {isEditing && (
              <div className="form-actions-account">
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? t('loading') : t('save')}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  {t('cancel')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="profile-tab-side">
        <div className="account-card membership-card">
          <div className="card-header-row">
            <h5 className="section-card-title">{t('account.organizations')}</h5>
          </div>
          <MembershipList authUser={authUser} t={t} />
        </div>
      </div>
    </div>
  );
});

const MembershipList = ({ authUser, t }) => {
  const roles = authUser.roles || {};
  const organizations = Object.entries(roles).filter(([, role]) => role !== null);

  if (organizations.length === 0) {
    return (
      <div className="membership-empty">
        <p className="empty-text">{t('account.no_organizations')}</p>
      </div>
    );
  }

  return (
    <div className="membership-list">
      {organizations.map(([name, role]) => (
        <div key={name} className="membership-item">
          <span className="membership-org-name">{name}</span>
          <span className={`role-badge ${role.toLowerCase()}`}>{role}</span>
        </div>
      ))}
    </div>
  );
};

// ── Organizations Tab ────────────────────────────────────────────────────────
// Full org management: pending invites, create/edit org, accordions, admin features
const OrganizationsTabBase = ({ authUser, t, firebase, onPendingInvitesChange }) => {
  const roles = authUser.roles || {};
  const organizations = Object.entries(roles).filter(([, role]) => role !== null);

  const [isCreateOrgModalOpen, setIsCreateOrgModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [isEditOrgModalOpen, setIsEditOrgModalOpen] = useState(false);

  const [openAccordions, setOpenAccordions] = useState(new Set());
  const [membersCache, setMembersCache] = useState({});
  const [membersLoading, setMembersLoading] = useState(new Set());
  const [membersError, setMembersError] = useState({});

  const [orgInfoCache, setOrgInfoCache] = useState({});
  const [orgInfoLoading, setOrgInfoLoading] = useState(new Set());
  const [orgInfoError, setOrgInfoError] = useState({});

  const [inviteEmail, setInviteEmail] = useState({});
  const [inviteRole, setInviteRole] = useState({});
  const [inviteMessage, setInviteMessage] = useState({});
  const [inviteLoading, setInviteLoading] = useState(new Set());

  const [deleteLoading, setDeleteLoading] = useState(new Set());

  const [pendingInvites, setPendingInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [inviteResponseLoading, setInviteResponseLoading] = useState(new Set());
  const [inviteResponseMessage, setInviteResponseMessage] = useState({ type: '', text: '' });

  const [invitesStatusCache, setInvitesStatusCache] = useState({});
  const [invitesStatusLoading, setInvitesStatusLoading] = useState(new Set());
  const [invitesStatusError, setInvitesStatusError] = useState({});

  const [highlightedInviteId, setHighlightedInviteId] = useState(null);
  const [handledNotice, setHandledNotice] = useState(null);

  // Report pending invites count up to parent (for tab badge)
  useEffect(() => {
    if (onPendingInvitesChange) {
      onPendingInvitesChange(pendingInvites.length);
    }
  }, [pendingInvites.length, onPendingInvitesChange]);

  useEffect(() => {
    let mounted = true;
    setInvitesLoading(true);
    firebase.doGetMyOrgInvites()
      .then((invites) => {
        if (!mounted) return;
        setPendingInvites((invites || []).filter((i) => i.status === 'pending'));
      })
      .catch(() => {
        if (!mounted) return;
        setPendingInvites([]);
      })
      .finally(() => {
        if (mounted) setInvitesLoading(false);
      });
    return () => { mounted = false; };
  }, [firebase]);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('invite');
    if (!token) return;

    firebase.doGetOrgInviteByToken(token)
      .then((invite) => {
        if (!invite || invite.status !== 'pending') {
          setHandledNotice(t('account.invite_already_handled'));
          return;
        }
        setPendingInvites((prev) => {
          const exists = prev.find((i) => i.inviteId === invite.inviteId);
          if (exists) {
            const filtered = prev.filter((i) => i.inviteId !== invite.inviteId);
            return [exists, ...filtered];
          }
          return [invite, ...prev];
        });
        setHighlightedInviteId(invite.inviteId);
        setTimeout(() => {
          const el = document.querySelector(`[data-invite-id="${invite.inviteId}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
      })
      .catch(() => {
        setHandledNotice(t('account.invite_already_handled'));
      });
  }, [firebase, t]);

  const getRoleClass = (role) => {
    switch (role?.toUpperCase()) {
      case 'ADMIN': return 'admin';
      case 'USER': return 'user';
      case 'GUEST': return 'guest';
      default: return 'user';
    }
  };

  const isAdmin = (role) => role?.toUpperCase() === 'ADMIN';

  const toggleAccordion = (name) => {
    setOpenAccordions((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
        if (!membersCache[name] && !membersLoading.has(name)) {
          loadMembers(name);
        }
        if (!invitesStatusCache[name] && !invitesStatusLoading.has(name)) {
          loadOrgInviteStatus(name);
        }
        if (!orgInfoCache[name] && !orgInfoLoading.has(name)) {
          loadOrgInfo(name);
        }
      }
      return next;
    });
  };

  const loadMembers = async (name) => {
    setMembersLoading((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
    try {
      const members = await firebase.doGetOrgMembers(name);
      setMembersCache((prev) => ({ ...prev, [name]: members }));
      setMembersError((prev) => ({ ...prev, [name]: '' }));
    } catch (error) {
      setMembersError((prev) => ({ ...prev, [name]: error.message }));
    }
    setMembersLoading((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const loadOrgInviteStatus = async (name) => {
    setInvitesStatusLoading((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
    try {
      const statuses = await firebase.doGetOrgInviteStatus(name);
      setInvitesStatusCache((prev) => ({ ...prev, [name]: statuses || [] }));
      setInvitesStatusError((prev) => ({ ...prev, [name]: '' }));
    } catch (error) {
      setInvitesStatusError((prev) => ({ ...prev, [name]: error.message }));
    }
    setInvitesStatusLoading((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const loadOrgInfo = async (name) => {
    setOrgInfoLoading((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
    try {
      const info = await firebase.doGetOrgInfo(name);
      setOrgInfoCache((prev) => ({ ...prev, [name]: info }));
      setOrgInfoError((prev) => ({ ...prev, [name]: '' }));
    } catch (error) {
      setOrgInfoError((prev) => ({ ...prev, [name]: error.message }));
    }
    setOrgInfoLoading((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const handleRespondToInvite = async (inviteId, accept) => {
    setInviteResponseLoading((prev) => {
      const next = new Set(prev);
      next.add(inviteId);
      return next;
    });
    try {
      await firebase.doRespondToOrgInvite(inviteId, accept);
      setPendingInvites((prev) => prev.filter((i) => i.inviteId !== inviteId));
      if (highlightedInviteId === inviteId) setHighlightedInviteId(null);
      setInviteResponseMessage({
        type: 'success',
        text: accept ? t('account.invite_accepted_message') : t('account.invite_declined_message'),
      });
      setTimeout(() => setInviteResponseMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setInviteResponseMessage({
        type: 'error',
        text: error.message || t('account.invite_response_error'),
      });
      setTimeout(() => setInviteResponseMessage({ type: '', text: '' }), 3000);
    }
    setInviteResponseLoading((prev) => {
      const next = new Set(prev);
      next.delete(inviteId);
      return next;
    });
  };

  const handleInvite = async (e, name) => {
    e.preventDefault();
    const email = inviteEmail[name] || '';
    const role = inviteRole[name] || 'USER';
    if (!email.trim()) {
      setInviteMessage((prev) => ({
        ...prev,
        [name]: { type: 'error', text: t('account.fill_required') },
      }));
      return;
    }
    setInviteLoading((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
    try {
      await firebase.doInviteUserToOrganization(name, email.trim(), role);
      setInviteEmail((prev) => ({ ...prev, [name]: '' }));
      setInviteRole((prev) => ({ ...prev, [name]: 'USER' }));
      setInviteMessage((prev) => ({
        ...prev,
        [name]: { type: 'success', text: t('account.invite_sent_success') },
      }));
      setTimeout(
        () =>
          setInviteMessage((prev) => ({
            ...prev,
            [name]: { type: '', text: '' },
          })),
        3000,
      );
      loadMembers(name);
    } catch (error) {
      setInviteMessage((prev) => ({
        ...prev,
        [name]: { type: 'error', text: error.message || t('account.invite_no_account_error') },
      }));
    }
    setInviteLoading((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const handleDeleteOrg = async (name) => {
    const confirmed = window.confirm(t('account.delete_org_confirm', { orgName: name }));
    if (!confirmed) return;
    setDeleteLoading((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
    try {
      await firebase.doDeleteOrganization(name);
    } catch (error) {
      alert(error.message);
    }
    setDeleteLoading((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const renderOrgMeta = (name) => {
    const info = orgInfoCache[name];
    if (!info || orgInfoError[name]) return null;
    const parts = [];
    if (info.contractorType) {
      parts.push(t(`account.contractor_type_${info.contractorType}`));
    }
    if (info.trade && Array.isArray(info.trade) && info.trade.length > 0) {
      parts.push(info.trade.map((tr) => t(`account.trade_${tr}`)).join(', '));
    }
    if (info.companySize) {
      parts.push(info.companySize);
    }
    if (info.country) {
      parts.push(info.country);
    }
    if (info.website) {
      parts.push(info.website);
    }
    if (info.createdAt) {
      parts.push(`${t('account.created')} ${new Date(info.createdAt).toLocaleDateString()}`);
    }
    if (parts.length === 0) return null;
    return (
      <p className="org-meta">{parts.join(' · ')}</p>
    );
  };

  const hasOrgs = organizations.length > 0;
  const hasInvites = pendingInvites.length > 0 || invitesLoading || handledNotice;

  return (
    <div className="organizations-tab-layout">
      <CreateOrgModal
        isOpen={isCreateOrgModalOpen}
        onClose={() => setIsCreateOrgModalOpen(false)}
        firebase={firebase}
      />

      <CreateOrgModal
        isOpen={isEditOrgModalOpen}
        onClose={() => {
          setIsEditOrgModalOpen(false);
          if (editingOrg) {
            setOrgInfoCache((prev) => ({ ...prev, [editingOrg]: null }));
            loadOrgInfo(editingOrg);
          }
          setEditingOrg(null);
        }}
        firebase={firebase}
        editMode
        initialOrgName={editingOrg || ''}
      />

      {!hasOrgs && !hasInvites && (
        <div className="org-empty-onboarding account-card">
          <div className="org-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h3 className="org-empty-title">{t('account.org_empty_title')}</h3>
          <p className="org-empty-description">{t('account.org_empty_description')}</p>
          <button
            type="button"
            className="btn btn-primary btn-large"
            onClick={() => setIsCreateOrgModalOpen(true)}
          >
            {t('account.create_org_button')}
          </button>
        </div>
      )}

      {hasInvites && (
        <div className="org-invites-section account-card">
          <div className="org-invites-header">
            <div className="org-invites-title-row">
              <h4 className="org-invites-title">{t('account.pending_invites_title')}</h4>
              {pendingInvites.length > 0 && (
                <span className="org-invites-count">{pendingInvites.length}</span>
              )}
            </div>
            <p className="org-invites-subtitle">{t('account.org_invites_subtitle')}</p>
          </div>

          {handledNotice && (
            <div className="message error org-invite-handled-notice">
              <span>⚠ {handledNotice}</span>
              <button
                className="btn btn-link"
                onClick={() => setHandledNotice(null)}
              >
                {t('hide')}
              </button>
            </div>
          )}

          {inviteResponseMessage.text && (
            <div className={`message ${inviteResponseMessage.type}`}>
              {inviteResponseMessage.type === 'success' ? '✓' : '⚠'} {inviteResponseMessage.text}
            </div>
          )}

          {invitesLoading ? (
            <div className="org-invites-list">
              {[1, 2].map((i) => (
                <div key={i} className="org-invite-card shimmer-row">
                  <div className="org-invite-card-header">
                    <div className="org-invite-icon shimmer-avatar" />
                    <div className="shimmer-lines" style={{ flex: 1 }}>
                      <div className="placeholder-line" style={{ width: '40%' }} />
                      <div className="placeholder-line short" style={{ width: '60%' }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="org-invites-list">
              {pendingInvites.map((invite) => (
                <div
                  key={invite.inviteId}
                  data-invite-id={invite.inviteId}
                  className={`org-invite-card ${highlightedInviteId === invite.inviteId ? 'org-invite-card--highlighted' : ''}`}
                >
                  <div className="org-invite-card-header">
                    <div className="org-invite-icon">
                      {invite.organization[0].toUpperCase()}
                    </div>
                    <div className="org-invite-meta">
                      <div className="org-invite-org-name">{invite.organization}</div>
                      <div className="org-invite-details">
                        <span className={`role-badge ${getRoleClass(invite.role)}`}>{invite.role}</span>
                        <span className="org-invite-by">{t('account.invited_by')} {invite.invitedByEmail}</span>
                        {invite.invitedAt && (
                          <span className="org-invite-date">
                            {new Date(invite.invitedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="org-invite-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleRespondToInvite(invite.inviteId, true)}
                      disabled={inviteResponseLoading.has(invite.inviteId)}
                    >
                      {inviteResponseLoading.has(invite.inviteId)
                        ? t('loading')
                        : t('account.invite_accept')}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleRespondToInvite(invite.inviteId, false)}
                      disabled={inviteResponseLoading.has(invite.inviteId)}
                    >
                      {t('account.invite_decline')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {hasOrgs && (
        <>
          {!hasInvites && (
            <div className="org-launcher-compact account-card">
              <div className="org-launcher-content">
                <div className="org-launcher-text">
                  <h5 className="section-card-title">{t('account.create_org_title')}</h5>
                  <p className="helper-text">{t('account.create_org_helper')}</p>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsCreateOrgModalOpen(true)}
                >
                  {t('account.create_org_button')}
                </button>
              </div>
            </div>
          )}

          <div className="org-cards-list">
            {organizations.map(([name, role]) => {
              const isOpen = openAccordions.has(name);
              const userIsAdmin = isAdmin(role);
              const members = membersCache[name] || [];
              const loadingMembers = membersLoading.has(name);
              const memberCount = members.length > 0 ? members.length : loadingMembers ? '—' : '0';

              return (
                <div key={name} className={`org-card account-card ${isOpen ? 'org-card--expanded' : ''}`}>
                  <button
                    className="org-card-header"
                    onClick={() => toggleAccordion(name)}
                    aria-expanded={isOpen}
                  >
                    <div className="org-card-identity">
                      <div className="org-card-icon">{name[0].toUpperCase()}</div>
                      <div className="org-card-info">
                        <div className="org-card-name">{name}</div>
                        {isOpen && renderOrgMeta(name)}
                      </div>
                    </div>
                    <div className="org-card-summary">
                      <span className="org-card-member-count">
                        {memberCount} {t('account.members_title')}
                      </span>
                      <span className={`role-badge ${getRoleClass(role)}`}>{role}</span>
                      <span className={`org-card-chevron ${isOpen ? 'org-card-chevron--open' : ''}`}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </span>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="org-card-body">
                      <div className="org-card-section">
                        <div className="org-card-section-header">
                          <h5 className="org-card-section-title">{t('account.members_title')}</h5>
                        </div>
                        {membersError[name] ? (
                          <div className="org-card-empty-state">
                            <p className="org-card-empty-text">{membersError[name]}</p>
                          </div>
                        ) : loadingMembers ? (
                          <div className="org-members-list">
                            {[1, 2, 3].map((i) => (
                              <div key={i} className="org-member-row shimmer-row">
                                <div className="shimmer-avatar" />
                                <div className="shimmer-lines">
                                  <div className="placeholder-line" style={{ width: '40%' }} />
                                  <div className="placeholder-line short" />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : members.length === 0 ? (
                          <div className="org-card-empty-state">
                            <p className="org-card-empty-text">{t('account.no_members')}</p>
                          </div>
                        ) : (
                          <div className="org-members-list">
                            {members.map((member) => (
                              <div key={member.uid} className="org-member-row">
                                <div className="member-avatar">
                                  {getMemberInitials(member)}
                                </div>
                                <div className="member-info">
                                  <span className="member-name">
                                    {member.username || member.email}
                                  </span>
                                  <span className="member-email">{member.email}</span>
                                </div>
                                <div className="member-badges">
                                  {member.emailVerified ? (
                                    <span className="verified-badge">{t('account.verified')}</span>
                                  ) : (
                                    <span className="not-verified-badge">{t('account.not_verified')}</span>
                                  )}
                                  <span className={`role-badge ${getRoleClass(member.role)}`}>
                                    {member.role}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {userIsAdmin && (
                        <>
                          <div className="org-card-divider" />
                          <div className="org-card-manage-section">
                            <h5 className="org-card-manage-title">{t('account.org_manage_section')}</h5>

                            <div className="org-card-section">
                              <div className="org-card-section-header">
                                <h6 className="org-card-section-title">{t('account.invitations_header')}</h6>
                              </div>
                              {invitesStatusError[name] ? (
                                <div className="org-card-empty-state">
                                  <p className="org-card-empty-text">{invitesStatusError[name]}</p>
                                </div>
                              ) : invitesStatusLoading.has(name) ? (
                                <div className="org-invitations-list">
                                  {[1, 2].map((i) => (
                                    <div key={i} className="org-invitation-row shimmer-row">
                                      <div className="shimmer-lines" style={{ width: '100%' }}>
                                        <div className="placeholder-line" style={{ width: '50%' }} />
                                        <div className="placeholder-line short" />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (invitesStatusCache[name] || []).length === 0 ? (
                                <div className="org-card-empty-state">
                                  <p className="org-card-empty-text">{t('account.no_invitations')}</p>
                                </div>
                              ) : (
                                <div className="org-invitations-list">
                                  {(invitesStatusCache[name] || []).map((invite) => (
                                    <div key={invite.inviteeUid || invite.inviteeEmail} className="org-invitation-row">
                                      <div className="invitation-info">
                                        <span className="invitation-email">{invite.inviteeEmail}</span>
                                        <span className={`role-badge ${getRoleClass(invite.role)}`}>{invite.role}</span>
                                      </div>
                                      <span className={`status-badge ${invite.status}`}>
                                        {invite.status === 'pending'
                                          ? t('account.status_pending')
                                          : invite.status === 'accepted'
                                            ? t('account.status_accepted')
                                            : t('account.status_declined')}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="org-card-section">
                              <div className="org-card-section-header">
                                <h6 className="org-card-section-title">{t('account.invite_member_title')}</h6>
                              </div>
                              <p className="org-card-section-helper">{t('account.invite_member_helper')}</p>
                              <form
                                onSubmit={(e) => handleInvite(e, name)}
                                className="org-invite-form"
                              >
                                <div className="org-invite-form-row">
                                  <input
                                    type="email"
                                    value={inviteEmail[name] || ''}
                                    onChange={(e) =>
                                      setInviteEmail((prev) => ({
                                        ...prev,
                                        [name]: e.target.value,
                                      }))
                                    }
                                    placeholder={t('account.email_placeholder')}
                                    className="org-invite-input"
                                  />
                                  <select
                                    value={inviteRole[name] || 'USER'}
                                    onChange={(e) =>
                                      setInviteRole((prev) => ({
                                        ...prev,
                                        [name]: e.target.value,
                                      }))
                                    }
                                    className="org-invite-select"
                                  >
                                    <option value="ADMIN">ADMIN</option>
                                    <option value="USER">USER</option>
                                    <option value="GUEST">GUEST</option>
                                  </select>
                                  <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={inviteLoading.has(name)}
                                  >
                                    {inviteLoading.has(name)
                                      ? t('loading')
                                      : t('account.invite_button')}
                                  </button>
                                </div>
                              </form>
                              {inviteMessage[name]?.text && (
                                <div className={`message ${inviteMessage[name].type}`}>
                                  {inviteMessage[name].type === 'success' ? '✓' : '⚠'}{' '}
                                  {inviteMessage[name].text}
                                </div>
                              )}
                            </div>

                            <div className="org-card-section">
                              <div className="org-edit-row">
                                <div>
                                  <h6 className="org-card-section-title">{t('account.edit_org_button')}</h6>
                                  <p className="org-card-section-helper">{t('account.org_settings_note')}</p>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingOrg(name);
                                    setIsEditOrgModalOpen(true);
                                  }}
                                >
                                  {t('account.edit_org_button')}
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="org-card-divider" />
                          <div className="org-card-danger-zone">
                            <h5 className="org-card-danger-title">{t('account.delete_org_button')}</h5>
                            <p className="org-card-danger-text">
                              {t('account.delete_org_confirm', { orgName: name })}
                            </p>
                            <button
                              className="btn btn-danger"
                              onClick={() => handleDeleteOrg(name)}
                              disabled={deleteLoading.has(name)}
                            >
                              {deleteLoading.has(name)
                                ? t('loading')
                                : t('account.delete_org_button')}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const OrganizationsTab = withFirebase(OrganizationsTabBase);

// ── Security Tab ─────────────────────────────────────────────────────────────
const SecurityTab = ({ t }) => {
  const [showReset, setShowReset] = useState(false);

  return (
    <div className="security-tab-layout">
      <div className="account-card">
        <div className="card-header-row">
          <h5 className="section-card-title">{t('account.change_password')}</h5>
        </div>
        <div className="password-section">
          <PasswordChangeForm />
        </div>
      </div>

      <div className="account-card">
        <div className="password-divider-header">
          <h5 className="section-card-title">{t('account.forgot_password')}</h5>
          <button
            className="btn btn-link"
            onClick={() => setShowReset(!showReset)}
          >
            {showReset ? t('account.hide') : t('account.show')}
          </button>
        </div>
        {showReset && <PasswordForgetForm />}
      </div>
    </div>
  );
};

// ── Connections Tab ──────────────────────────────────────────────────────────
// Functional component (converted from class)
const ConnectionsTabBase = ({ authUser, firebase, t }) => {
  const [activeSignInMethods, setActiveSignInMethods] = useState([]);
  const [error, setError] = useState(null);

  const fetchSignInMethods = useCallback(() => {
    firebase.auth
      .fetchSignInMethodsForEmail(authUser.email)
      .then(methods => {
        setActiveSignInMethods(methods);
        setError(null);
      })
      .catch(err => setError(err));
  }, [firebase, authUser.email]);

  useEffect(() => {
    fetchSignInMethods();
  }, [fetchSignInMethods]);

  const onSocialLoginLink = provider => {
    firebase.auth.currentUser
      .linkWithPopup(firebase[provider])
      .then(fetchSignInMethods)
      .catch(err => setError(err));
  };

  const onDefaultLoginLink = password => {
    const credential = firebase.emailAuthProvider.credential(
      authUser.email,
      password,
    );

    firebase.auth.currentUser
      .linkAndRetrieveDataWithCredential(credential)
      .then(fetchSignInMethods)
      .catch(err => setError(err));
  };

  const onUnlink = providerId => {
    firebase.auth.currentUser
      .unlink(providerId)
      .then(fetchSignInMethods)
      .catch(err => setError(err));
  };

  const getMethodIcon = (methodId) => {
    switch (methodId) {
      case 'google.com': return 'G';
      case 'microsoft.com': return 'M';
      case 'facebook.com': return 'f';
      case 'twitter.com': return 'X';
      case 'password': return 'Pw';
      default: return '?';
    }
  };

  const getMethodName = (methodId) => {
    switch (methodId) {
      case 'google.com': return 'Google';
      case 'microsoft.com': return 'Microsoft';
      case 'facebook.com': return 'Facebook';
      case 'twitter.com': return 'Twitter/X';
      case 'password': return 'Email & Password';
      default: return methodId;
    }
  };

  const getIconClass = (methodId) => {
    switch (methodId) {
      case 'google.com': return 'google';
      case 'microsoft.com': return 'microsoft';
      case 'facebook.com': return 'facebook';
      case 'twitter.com': return 'twitter';
      case 'password': return 'password';
      default: return 'password';
    }
  };

  return (
    <div className="connections-tab-layout">
      <div className="account-card">
        <div className="card-header-row">
          <h5 className="section-card-title">{t('account.sign_in_methods_title')}</h5>
        </div>

        {error && (
          <div className="message error">⚠ {error.message}</div>
        )}

        <div className="signin-methods-grid">
          {SIGN_IN_METHODS.map(signInMethod => {
            const onlyOneLeft = activeSignInMethods.length === 1;
            const isEnabled = activeSignInMethods.includes(signInMethod.id);

            return (
              <div key={signInMethod.id} className="signin-method-item">
                <div className="signin-method-info">
                  <div className={`signin-method-icon ${getIconClass(signInMethod.id)}`}>
                    {getMethodIcon(signInMethod.id)}
                  </div>
                  <div className="signin-method-details">
                    <strong>{getMethodName(signInMethod.id)}</strong>
                    <span className={`method-status ${isEnabled ? 'connected' : 'disconnected'}`}>
                      {isEnabled ? `✓ ${t('account.connected')}` : t('account.not_connected')}
                    </span>
                  </div>
                </div>
                {signInMethod.id !== 'password' && (
                  isEnabled ? (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => onUnlink(signInMethod.id)}
                      disabled={onlyOneLeft}
                      title={onlyOneLeft ? t('account.cant_unlink_last') : ''}
                    >
                      {t('account.unlink')}
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => onSocialLoginLink(signInMethod.provider)}
                    >
                      {t('account.link_button')}
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>

        {!activeSignInMethods.includes('password') && (
          <div className="password-link-form">
            <PasswordLinkForm
              onLink={onDefaultLoginLink}
              t={t}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ── Password Link Form ───────────────────────────────────────────────────────
// Functional component (converted from class)
const PasswordLinkForm = ({ onLink, t }) => {
  const [passwordOne, setPasswordOne] = useState('');
  const [passwordTwo, setPasswordTwo] = useState('');
  const [showForm, setShowForm] = useState(false);

  const onSubmit = event => {
    event.preventDefault();
    onLink(passwordOne);
    setPasswordOne('');
    setPasswordTwo('');
    setShowForm(false);
  };

  const isInvalid = passwordOne !== passwordTwo || passwordOne === '';

  if (!showForm) {
    return (
      <div className="add-password-cta">
        <button
          className="btn btn-secondary"
          onClick={() => setShowForm(true)}
        >
          {t('account.add_password_login')}
        </button>
      </div>
    );
  }

  return (
    <div>
      <h4>{t('account.set_password_title')}</h4>
      <form onSubmit={onSubmit}>
        <div className="form-group">
          <label>{t('account.new_password_label')}</label>
          <input
            name="passwordOne"
            value={passwordOne}
            onChange={(e) => setPasswordOne(e.target.value)}
            type="password"
            placeholder={t('account.new_password_placeholder')}
          />
        </div>
        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label>{t('account.confirm_password_label')}</label>
          <input
            name="passwordTwo"
            value={passwordTwo}
            onChange={(e) => setPasswordTwo(e.target.value)}
            type="password"
            placeholder={t('account.confirm_password_placeholder')}
          />
        </div>
        <div className="form-actions" style={{ marginTop: '1.25rem' }}>
          <button className="btn btn-primary" disabled={isInvalid} type="submit">
            {t('account.set_password')}
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => {
              setShowForm(false);
              setPasswordOne('');
              setPasswordTwo('');
            }}
          >
            {t('cancel')}
          </button>
        </div>
      </form>
    </div>
  );
};

// Wrap ConnectionsTab with Firebase + Translation HOCs
const withTranslationHOC = (Component) => {
  return (props) => {
    const { t } = useTranslation();
    return <Component {...props} t={t} />;
  };
};

const ConnectionsTab = withTranslationHOC(withFirebase(ConnectionsTabBase));

// ── Main Account Page Component ──────────────────────────────────────────────
const AccountPageBase = ({ firebase }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('profile');
  const [pendingInvitesCount, setPendingInvitesCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    firebase.doGetMyOrgInvites()
      .then((invites) => {
        if (!mounted) return;
        setPendingInvitesCount((invites || []).filter((i) => i.status === 'pending').length);
      })
      .catch(() => {
        if (mounted) setPendingInvitesCount(0);
      });
    return () => { mounted = false; };
  }, [firebase]);

  const handlePendingInvitesChange = useCallback((count) => {
    setPendingInvitesCount(count);
  }, []);

  const renderTabContent = (authUser) => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab authUser={authUser} t={t} />;
      case 'organizations':
        return (
          <OrganizationsTab
            authUser={authUser}
            t={t}
            onPendingInvitesChange={handlePendingInvitesChange}
          />
        );
      case 'security':
        return <SecurityTab t={t} />;
      case 'connections':
        return <ConnectionsTab authUser={authUser} />;
      default:
        return <ProfileTab authUser={authUser} t={t} />;
    }
  };

  return (
    <AuthUserContext.Consumer>
      {authUser => {
        const avatarInfo = getAvatarInfo(authUser);
        const roles = authUser.roles || {};
        const userOrganizations = Object.entries(roles).filter(([, role]) => role !== null);

        return (
          <div className='account-page main'>
            {/* Identity Hero */}
            <div className="identity-hero">
              <div className="identity-avatar">
                {avatarInfo.hasPhoto ? (
                  <img src={avatarInfo.photoURL} alt={avatarInfo.displayName || 'Profile'} />
                ) : (
                  avatarInfo.initials
                )}
              </div>
              <div className="identity-info">
                <h1 className="identity-name">
                  {avatarInfo.displayName || authUser.username || authUser.email}
                </h1>
                <p className="identity-email">{authUser.email}</p>
                <div className="identity-badges">
                  <span className={`email-badge ${authUser.emailVerified ? 'verified' : 'not-verified'}`}>
                    <span className="badge-dot" />
                    {authUser.emailVerified ? t('account.verified') : t('account.not_verified')}
                  </span>
                  {userOrganizations.map(([name, role]) => (
                    <span key={name} className={`role-pill ${role.toLowerCase()}`}>
                      {name} · {role}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="account-tabs" role="tablist">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="tab-label">{t(tab.labelKey)}</span>
                  {tab.id === 'organizations' && pendingInvitesCount > 0 && (
                    <span className="tab-badge">{pendingInvitesCount}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="tab-content" role="tabpanel" key={activeTab}>
              {renderTabContent(authUser)}
            </div>
          </div>
        );
      }}
    </AuthUserContext.Consumer>
  );
};

const AccountPage = withFirebase(AccountPageBase);

const condition = authUser => !!authUser;

const HOCsAppliedToHomePage = withAuthorization(condition)(AccountPage);
const FinalHomePageComponent = withEmailVerification(HOCsAppliedToHomePage);

export default FinalHomePageComponent;
